import {
  CHART_PREFIX,
  getChartAndTable,
  getChartDimensions,
  getNextChartIndex,
  setChartTitleAndSubtitle,
  upsertChartTable,
} from "./helpers/common";
import { ChartIcon, ChartIconGetter, ChartShape, ChartSize, EdiphiChartType } from "./api";
import { getEstimateReportData } from "../workbook";
import { fetchEstimateExport } from "../../api";
import { transformUseGroups } from "../datasources/useGroups";
import { colorFade, dataVizColorArray } from "../styles/colors";
import { NotFoundError } from "../../errors";

const SPACE_ALLOCATION_CHART_TYPE = "spaceAllocation";

const chartBuilder = async () => {
  const chartIndex = await getNextChartIndex();
  const chartName = `${CHART_PREFIX}${SPACE_ALLOCATION_CHART_TYPE}_${chartIndex}`;

  await upsertChartData(chartName);

  const chart = await addChart({
    chartType: Excel.ChartType.barStacked,
    chartTitle: "Units & Rooms vs GSF",
    chartSubTitle: "",
    name: chartName,
    shape: "photoLandscape",
    size: "medium",
  });

  return chart;
};

const chartRefresher = async (worksheetName: string, chartName: string) => {
  await upsertChartData(chartName);
  await Excel.run(async (context) => {
    const [chart, table] = await getChartAndTable(context, worksheetName, chartName);
    chart.setData(table.getRange());
    await context.sync();
  });
};

const upsertChartData = async (chartName: string) => {
  const createDataRows = async () => {
    const reportData = await getEstimateReportData();
    const estimateId = reportData[0]?.estimate_id;
    if (!estimateId) return [];
    const result = await fetchEstimateExport(estimateId);
    if (!result.success || !result.data) {
      throw new NotFoundError("Estimate data not found for chart", { estimateId });
    }
    const data = transformUseGroups(result.data.estimate?.stats?.useGroups ?? [], result.data.floor_groups ?? []);
    const dataRows: Record<string, string | number | null>[] = [];
    let maxBarAmount = 0;
    data.forEach((row) => {
      let rooms = row?.room_area ?? 0;
      let units = row?.nsf ?? 0;
      let gsf = row?.gsf ?? 0;
      let unfilled = 0;
      let rooms_over = 0;
      let units_over = 0;
      maxBarAmount = Math.max(maxBarAmount, rooms + units, gsf);
      if (rooms >= gsf) {
        rooms = gsf;
        units_over = units;
        units = 0;
        rooms_over = rooms - gsf;
      } else if (rooms + units >= gsf) {
        units_over = units - (gsf - rooms);
        units = gsf - rooms;
      } else if (rooms + units < gsf) {
        unfilled = gsf - (rooms + units);
      }
      // creates a bold line for total gsf
      const totalBarThickness = maxBarAmount > 100000 ? 2000 : maxBarAmount > 50000 ? 1000 : 500;
      const total = gsf > 0 ? totalBarThickness : 0;
      dataRows.push({
        "Use Group": row.name,
        rooms: rooms || "",
        units: units || "",
        unfilled: unfilled || "",
        gsf: total || "",
        "rooms over": rooms_over || "",
        "units over": units_over || "",
      });
    });
    return dataRows;
  };
  const dataRows = await createDataRows();
  await upsertChartTable(dataRows, chartName);
};

const addChart = async (params: {
  chartType: Excel.ChartType;
  chartTitle: string;
  chartSubTitle: string;
  name: string;
  shape: ChartShape;
  size: ChartSize;
}) => {
  return Excel.run(async (context) => {
    const { chartType, chartTitle, chartSubTitle, name, shape, size } = params;
    const activeSheet = context.workbook.worksheets.getActiveWorksheet();
    const table = context.workbook.tables.getItemOrNullObject(name);
    await context.sync();
    if (table.isNullObject) throw new NotFoundError("Table not found", { tableName: name });

    const range = table.getRange();
    await context.sync();

    const chart = activeSheet.charts.add(chartType, range, Excel.ChartSeriesBy.columns);
    chart.name = name;
    setChartTitleAndSubtitle(chart, chartTitle, chartSubTitle);
    const { width, height } = getChartDimensions(shape, size);
    chart.height = height;
    chart.width = width;
    styleChart(chart);
    await context.sync();
    return chart;
  });
};

const styleChart = (chart: Excel.Chart) => {
  const roomsColor = dataVizColorArray[0].replace("#", "");
  const unitsColor = dataVizColorArray[1].replace("#", "");
  const roomsOverColor = colorFade(roomsColor, 0.5).replace("#", "");
  const unitsOverColor = colorFade(unitsColor, 0.25).replace("#", "");
  const roomsSeries = chart.series.getItemAt(0);
  const unitsSeries = chart.series.getItemAt(1);
  const unfilledSeries = chart.series.getItemAt(2);
  const totalSeries = chart.series.getItemAt(3);
  const roomsOverSeries = chart.series.getItemAt(4);
  const unitsOverSeries = chart.series.getItemAt(5);
  roomsSeries.format.fill.setSolidColor(roomsColor);
  roomsSeries.format.line.color = roomsColor;
  roomsSeries.format.line.weight = 1;
  unitsSeries.format.fill.setSolidColor(unitsColor);
  unitsSeries.format.line.color = unitsColor;
  unitsSeries.format.line.weight = 1;
  unfilledSeries.format.fill.clear();
  unfilledSeries.format.line.lineStyle = Excel.ChartLineStyle.dash;
  unfilledSeries.format.line.color = "A6A6A6";
  unfilledSeries.format.line.weight = 1;
  totalSeries.format.fill.setSolidColor("black");
  totalSeries.format.line.color = "black";
  totalSeries.format.line.weight = 2;
  totalSeries.gapWidth = 20;
  roomsOverSeries.format.fill.setSolidColor(roomsOverColor);
  roomsOverSeries.format.line.color = roomsColor;
  roomsOverSeries.format.line.weight = 1;
  unitsOverSeries.format.fill.setSolidColor(unitsOverColor);
  unitsOverSeries.format.line.color = unitsColor;
  unitsOverSeries.format.line.weight = 1;
  chart.axes.categoryAxis.format.line.color = "black";
  chart.axes.categoryAxis.format.line.weight = 1;
  chart.axes.categoryAxis.format.font.bold = true;
  chart.axes.categoryAxis.format.font.size = 11;
  chart.axes.valueAxis.format.font.size = 10;
  chart.axes.valueAxis.numberFormat = '[>999999]0.0,,"M sf";[>999]0,"k sf";0';
  chart.legend.visible = true;
  chart.legend.position = Excel.ChartLegendPosition.bottom;
  chart.legend.format.font.size = 12;
  chart.legend.format.font.bold = true;
};

const chartIconGetter: ChartIconGetter = (): ChartIcon => {
  return { src: "assets/icons/FcSpaceAllocation.svg", alt: "Space Allocation" };
};

export const spaceAllocationChartType: EdiphiChartType = {
  key: SPACE_ALLOCATION_CHART_TYPE,
  title: "Units & Rooms vs GSF",
  chartBuilder,
  chartRefresher,
  chartIconGetter,
  getOptions: async () => Promise.resolve({}),
};
